// @ts-check
/**
 * Shared DOM builder (DS-02). Replaces the per-module `node()`/`button()` helpers.
 *
 * h("button", { class: "ui-button", type: "button", onClick, aria: { label: "Close" } }, "Close")
 *
 * Props: `class`, `text`, `dataset`, `aria` (aria-* attributes), `style` (custom
 * properties only, e.g. { "--bunny-color": value }), `on<Event>` listeners, boolean
 * attributes (`hidden`, `disabled`), and any other attribute. Children may be
 * strings, nodes, arrays, or null/false (skipped).
 */

/** @typedef {string | Node | null | undefined | false | readonly Child[]} Child */

/**
 * @template {keyof HTMLElementTagNameMap} K
 * @param {K} tag
 * @param {Record<string, any>} [props]
 * @param {...Child} children
 * @returns {HTMLElementTagNameMap[K]}
 */
export function h(tag, props = {}, ...children) {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(props || {})) {
    if (value === undefined || value === null || value === false) continue;
    if (key === "class" || key === "className") {
      element.className = value;
    } else if (key === "text") {
      element.textContent = value;
    } else if (key === "dataset") {
      for (const [name, data] of Object.entries(value)) {
        if (data !== undefined) element.dataset[name] = String(data);
      }
    } else if (key === "aria") {
      for (const [name, data] of Object.entries(value)) {
        if (data !== undefined && data !== null)
          element.setAttribute(`aria-${name}`, String(data));
      }
    } else if (key === "style") {
      for (const [name, data] of Object.entries(value))
        element.style.setProperty(name, String(data));
    } else if (/^on[A-Z]/.test(key) && typeof value === "function") {
      element.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (value === true) {
      element.setAttribute(key, "");
    } else if (key in element && typeof value !== "string") {
      /** @type {any} */ (element)[key] = value;
    } else {
      element.setAttribute(key, String(value));
    }
  }
  append(element, children);
  return element;
}

/** @param {Element | DocumentFragment} parent @param {readonly Child[]} children */
export function append(parent, children) {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    if (Array.isArray(child)) append(parent, child);
    else
      parent.append(
        typeof child === "string" ? child : /** @type {Node} */ (child),
      );
  }
  return parent;
}

let sequence = 0;
/** Stable unique id for label/description wiring. @param {string} prefix */
export function uid(prefix = "ui") {
  sequence += 1;
  return `${prefix}-${sequence}`;
}

/**
 * Attach accessors and methods to an element. Object.assign would copy getter
 * results instead of the getters themselves.
 * @template {object} T
 * @template {object} U
 * @param {T} target
 * @param {U} members
 * @returns {T & U}
 */
export function extend(target, members) {
  return /** @type {T & U} */ (
    Object.defineProperties(target, Object.getOwnPropertyDescriptors(members))
  );
}
